import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ColorNeutralDarkGrey, FontWeightNormal } from 'autogen/design-tokens/tokens';
import type { ProductSubscriptionInfoViewModel } from 'autogen/swagger/all';
import { NotClubMatasPrice, PriceBefore, YourDiscount } from 'autogen/translation-keys/trans-website-product-priceinfo';
import { BuyOnce } from 'autogen/translation-keys/trans-website-product-subscription';
import { PossibleAdditionalDeliveryCost } from 'autogen/translation-keys/trans-website-shared-productitem';
import Card from 'components/generic/card';
import { Typography } from 'components/generic/typography';
import { SubscriptionContext } from 'components/pages/PDP/components/MidSection/hooks/context';
import { useProductStore } from 'components/pages/PDP/shared/productStore';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import { inputValues } from '../shared/enums';
import { AdditionalLine, Content, Headline, Label, LineThrough, Price, StyledCard, StyledRadio } from './components';

const PriceNote = styled.div`
    color: ${ColorNeutralDarkGrey};
    font-weight: ${FontWeightNormal};
`;

const PricePreFix = styled.span<{ $isFixedLowPrice?: boolean; $isMultiBuyPrice?: boolean }>`
    ${({ $isFixedLowPrice }) =>
        $isFixedLowPrice &&
        css`
               color: ${({ theme }) => theme.ComponentsLabelFixedPriceTextInverted};
           `}

    ${({ $isMultiBuyPrice }) =>
        $isMultiBuyPrice &&
        css`
            color: ${({ theme }) => theme.ColorTextSale};
        `}
`;

export default function BuyOnceCard(model: ProductSubscriptionInfoViewModel): JSX.Element {
    const {
        priceInfo: { price, onDiscount, priceLabel, priceBefore, discount },
        showPossibleAdditionalDeliveryCost
    } = model;
    const { subscriptionActive, setSubscriptionActive } = useContext(SubscriptionContext);
    const { setIsSubscription } = useProductStore();
    const { t } = useTranslation();
    const isClubMatasOnlyPrice = priceLabel?.type === 'ClubMatasOnlyPrice';
    const isMatasPlusPrice = priceLabel?.type === 'MatasPlusPrice';
    const isFixedLowPrice = priceLabel?.type === 'FixedLowPrice';
    const isMultiBuyPrice = priceLabel?.type === 'MultiBuyPrice';
    const priceBeforeLabel = isClubMatasOnlyPrice ? t(NotClubMatasPrice) : t(PriceBefore);

    function switchSelection(type: inputValues) {
        setSubscriptionActive(type === inputValues.subscription);
        setIsSubscription(type === inputValues.subscription);

        GA4Tracking({
            eventName: 'toggle_subscription',
            context: 'product_page',
            category: 'ecommerce',
            eventParams: [{ param_name: 'toggle', param_value: type === inputValues.subscription }]
        });
    }

    return (
        <StyledCard forwardedAs="label" $isActive={!subscriptionActive}>
            <Card.Content>
                <Headline $isActive={!subscriptionActive} forwardedAs="div">
                    <StyledRadio
                        name="ProductPaymentChoice"
                        value={inputValues.default}
                        onChange={() => switchSelection(inputValues.default)}
                        defaultChecked={!subscriptionActive}
                    />

                    <Label>{t(BuyOnce)}</Label>

                    <Price $isClubPrice={isClubMatasOnlyPrice || isMatasPlusPrice}>
                        {priceLabel && (
                            <PricePreFix $isFixedLowPrice={isFixedLowPrice} $isMultiBuyPrice={isMultiBuyPrice}>
                                {priceLabel.text}{' '}
                            </PricePreFix>
                        )}
                        {price}
                        {showPossibleAdditionalDeliveryCost && !subscriptionActive && (
                            <PriceNote>{t(PossibleAdditionalDeliveryCost)}</PriceNote>
                        )}
                    </Price>
                </Headline>

                {!subscriptionActive && onDiscount && (
                    <Content>
                        <AdditionalLine>
                            <Typography.Body as="span">{priceBeforeLabel}</Typography.Body>
                            <LineThrough forwardedAs="span">{priceBefore}</LineThrough>
                        </AdditionalLine>

                        <AdditionalLine>
                            <Typography.Body size="M" strong as="span">
                                {t(YourDiscount)}
                            </Typography.Body>
                            <Typography.Body size="M" strong as="span">
                                {discount}
                            </Typography.Body>
                        </AdditionalLine>
                    </Content>
                )}
            </Card.Content>
        </StyledCard>
    );
}
